import { DatePipe, DecimalPipe } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import { PrintLabelsDialogComponent } from "@components/print-labels-dialog/print-labels-dialog.component";
import { environment } from "@environments/environment";
import { AppStateService, RequestService } from "@services/index";

@Component({
  selector: "fh-order-details",
  templateUrl: "./order-details.component.html",
  styleUrls: ["./order-details.component.css"],
  standalone: true,
  imports: [
    DecimalPipe,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatDialogModule,
    FormAutocompleteComponent,
  ],
})
export class OrderDetailsComponent implements OnInit {
  list: any;
  token = "";
  cname = "";
  checked = false;
  success_msg = "";
  error_msg = "";
  customer_name = "";
  shipping_address = "";
  billing_address = "";
  shipping_email = "";
  billing_email = "";
  customer_phone = "";
  sub_total = 0;
  dtm = 0;
  shipping_total = 0;
  total = 0;
  created_on = "";
  currency = "";
  orderStatus = "";
  payment_status = "";
  app_url = environment.backendURL;
  id: any;
  updated_on = "";
  button_disabled = false;
  cancellation_rason = "";
  access = "";
  user_id = "";
  final_price = 0;
  deliveryDiscountAmount: any;
  offerCodeDetails: any;
  deliveryZoneDiscount!: number;
  adjustedShippingAmount: number | null = null;
  remarks!: string;
  adminNotes!: string;
  /** Each merchant's note to the customer on this order (shown on My Orders). */
  merchantRemarks: { vendor_id: number; vendor_name: string; remarks: string }[] = [];
  /** Merchant remarks as last saved, by vendor id, so an unchanged text is not re-sent. */
  private savedMerchantRemarks = new Map<number, string>();
  /** Vendor ids whose remarks are being saved: one save at a time per merchant. */
  savingMerchantRemarks = new Set<number>();
  // Bucketed discounts from the backend (subtotal / delivery incl. zonal / subtotal+delivery)
  subtotalDiscount = 0;
  deliveryDiscount = 0;
  subtotalDeliveryDiscount = 0;
  offerCode: string | null = null;

  deliveryStatusOptions: FormAutocompleteOption<string>[] = [
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
    { value: "refunded", label: "Refunded" },
  ];

  updateItemDeliveryStatus(item: any) {
    this.requestService
      .post("/api/v1/update_item_delivery_status", {
        orderDetailId: item.id,
        deliveryStatus: item.delivery_status,
      })
      .subscribe((res: any) => {
        if (res.status === true) {
          this.success_msg = "Item delivery status updated";
          setTimeout(() => {
            this.success_msg = "";
          }, 3000);
        } else {
          this.error_msg = "Failed to update";
          setTimeout(() => {
            this.error_msg = "";
          }, 3000);
        }
      });
  }

  updateItemRemarks(item: any) {
    this.requestService
      .post("/api/v1/update_item_remarks", {
        orderDetailId: item.id,
        remarks: item.remarks,
      })
      .subscribe((res: any) => {
        if (res.status === true) {
          this.success_msg = "Item remarks updated";
          setTimeout(() => {
            this.success_msg = "";
          }, 3000);
        } else {
          this.error_msg = "Failed to update";
          setTimeout(() => {
            this.error_msg = "";
          }, 3000);
        }
      });
  }

  constructor(
    private router: Router,
    private requestService: RequestService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private appStateService: AppStateService
  ) {
    this.id = this.route.snapshot.paramMap.get("id");
    this.cname = this.route.snapshot.queryParamMap.get("cname")!;
    const navigation = this.router.getCurrentNavigation()!;
    const state = navigation.extras.state as {
      success_msg: string;
      error_msg: string;
    };
    if (state) {
      if (state.success_msg) {
        this.success_msg = state.success_msg;
        setTimeout(() => {
          this.success_msg = "";
        }, 3000);
      } else if (state.error_msg) {
        this.error_msg = state.error_msg;
        setTimeout(() => {
          this.error_msg = "";
        }, 3000);
      }
    }
  }

  /** Shipping labels for this order: every merchant's parcel, or one merchant's. */
  printLabels(vendorId?: number): void {
    if (!this.id) return;
    this.dialog.open(PrintLabelsDialogComponent, {
      data: { orderIds: [this.id], vendorId },
      width: "420px",
    });
  }

  ngOnInit(): void {
    this.fetchOrder();
  }

  saveMerchantRemarks(m: { vendor_id: number; remarks: string }): void {
    if (this.savingMerchantRemarks.has(m.vendor_id)) return;
    const sent = (m.remarks || "").slice(0, 500);
    if (sent === this.savedMerchantRemarks.get(m.vendor_id)) {
      this.success_msg = "Merchant remarks already saved";
      return;
    }
    this.savingMerchantRemarks.add(m.vendor_id);
    this.requestService
      .post("/api/v1/update_merchant_remarks_for_order", {
        orderId: this.id,
        vendorId: m.vendor_id,
        remarks: sent,
      })
      .subscribe({
        next: (res: any) => {
          this.savingMerchantRemarks.delete(m.vendor_id);
          if (res.status) {
            this.savedMerchantRemarks.set(m.vendor_id, sent);
            // Typed on while it saved: what is in the box now is not what was saved.
            this.success_msg =
              (m.remarks || "").slice(0, 500) === sent
                ? "Merchant remarks updated"
                : "Merchant remarks updated - newer edits are not saved yet";
          } else {
            this.error_msg = res.message || "Failed to update merchant remarks";
          }
        },
        error: () => {
          this.savingMerchantRemarks.delete(m.vendor_id);
          this.error_msg = "Failed to update merchant remarks";
        },
      });
  }

  saveAdminNotes(): void {
    this.requestService
      .post("/api/v1/update_admin_notes_for_order", { adminNotes: this.adminNotes, orderId: this.id })
      .subscribe((res: any) => {
        if (res.result_code === 1) {
          this.success_msg = "Admin notes updated";
          setTimeout(() => (this.success_msg = ""), 3000);
        } else {
          this.error_msg = "Failed to update admin notes";
          setTimeout(() => (this.error_msg = ""), 3000);
        }
      });
  }

  saveRemarks(): void {
    this.requestService
      .post("/api/v1/update_remarks_for_order", { orderId: this.id, remarks: this.remarks })
      .subscribe((res: any) => {
        if (res.result_code === 1) {
          this.success_msg = "Remarks updated";
          setTimeout(() => (this.success_msg = ""), 3000);
        } else {
          this.error_msg = "Failed to update remarks";
          setTimeout(() => (this.error_msg = ""), 3000);
        }
      });
  }

  fetchOrder() {
    this.access = this.appStateService.currentUser$.value?.access ?? "";
    this.user_id = String(this.appStateService.currentUser$.value?.id ?? "");
    this.requestService.get(`/api/v2/order-details/${this.id}`).subscribe((res: any) => {
      if (res.result_code === 1) {
        this.list = res.data.orderDetails.details;
        this.customer_name = res.data.orderDetails.customer_name;
        this.shipping_address = res.data.orderDetails.shipping_address;
        this.billing_address = res.data.orderDetails.billing_address;
        this.shipping_email = res.data.orderDetails.shipping_email;
        this.billing_email = res.data.orderDetails.billing_email;
        this.customer_phone = res.data.orderDetails.mobile;
        this.sub_total = res.data.orderDetails.sub_total;
        this.shipping_total = res.data.orderDetails.shipping_total;
        this.total = res.data.orderDetails.total;
        this.created_on = res.data.orderDetails.created_on;
        this.updated_on = res.data.orderDetails.updated_on;
        this.currency = res.data.orderDetails.currency;
        this.orderStatus = res.data.orderDetails.order_status;
        this.payment_status = res.data.orderDetails.payment_status;
        this.cancellation_rason = res.data.orderDetails.cancel_reason;
        this.final_price = res.data.orderDetails.final_price;
        this.deliveryDiscountAmount = res.data.orderDetails?.delivery_discount_amount;
        this.offerCodeDetails = res.data.orderDetails?.offerCodeDetails;
        this.deliveryZoneDiscount = +res.data.orderDetails.delivery_zone_discount
          ? +res.data.orderDetails.delivery_zone_discount
          : 0;
        this.adjustedShippingAmount =
          res.data.orderDetails.adjusted_shipping_fee != null
            ? +res.data.orderDetails.adjusted_shipping_fee
            : null;
        this.remarks = res.data.orderDetails.remarks;
        this.adminNotes = res.data.orderDetails.admin_notes;
        this.merchantRemarks = res.data.orderDetails.merchant_remarks ?? [];
        this.savedMerchantRemarks = new Map(
          this.merchantRemarks.map((m) => [m.vendor_id, m.remarks || ""])
        );
        this.dtm = res.data.orderDetails.destination_transhipper_markup;
        this.subtotalDiscount = +res.data.orderDetails.subtotalDiscount || 0;
        this.deliveryDiscount = +res.data.orderDetails.deliveryDiscount || 0;
        this.subtotalDeliveryDiscount = +res.data.orderDetails.subtotalDeliveryDiscount || 0;
        this.offerCode =
          res.data.orderDetails.offer_code ||
          res.data.orderDetails.offerCodeDetails?.offerCode ||
          null;
      }
    });
  }

  fetchDeliveryRemarks = async () => {
    this.requestService.get(`/api/v1/delivery-remarks?orderId=${this.id}`);
  };

  order_status(status: string, reason?: string) {
    this.button_disabled = true;
    const data = {
      status: status,
      reason: reason || this.cancellation_rason,
      id: this.id,
    };
    this.requestService.post("/api/v2/order-status", data).subscribe((res: any) => {
      if (res.status === true) {
        this.orderStatus = res.data.order_status;
        this.button_disabled = false;
      } else {
        this.error_msg = res.message;
        this.button_disabled = false;
      }
    });
  }

  cancelShowPopup() {
    if (this.orderStatus === "cancelled") {
      // Just show the reason in a simple dialog
      this.dialog.open(CancelReasonDialogComponent, {
        width: "500px",
        data: { reason: this.cancellation_rason, viewOnly: true },
      });
    } else {
      // Show cancel confirmation dialog
      const dialogRef = this.dialog.open(CancelReasonDialogComponent, {
        width: "500px",
        data: { reason: this.cancellation_rason, viewOnly: false },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.cancellation_rason = result;
          this.order_status("cancelled", result);
        }
      });
    }
  }
}

// Cancel Reason Dialog Component
@Component({
  selector: "fh-cancel-reason-dialog",
  template: `
    <h2 mat-dialog-title>
      @if (data.viewOnly) { Order Cancellation Reason } @else { Are you sure? Cancel this
      order. }
    </h2>
    <mat-dialog-content>
      @if (data.viewOnly) {
      <p class="cancel-reason-text">{{ data.reason }}</p>
      } @else {
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Cancellation Reason</mat-label>
        <textarea
          matInput
          [(ngModel)]="cancellationReason"
          rows="4"
          placeholder="Enter the reason for cancelling this order"
        ></textarea>
        @if (showError && !cancellationReason) {
        <mat-error>Cancellation reason is required</mat-error>
        }
      </mat-form-field>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      @if (data.viewOnly) {
      <button mat-raised-button mat-dialog-close color="primary">Close</button>
      } @else {
      <button mat-button mat-dialog-close>No</button>
      <button mat-raised-button color="warn" (click)="onConfirm()">Yes, Cancel Order</button>
      }
    </mat-dialog-actions>
  `,
  styles: [
    `
      .full-width {
        width: 100%;
      }
      .cancel-reason-text {
        margin: 0;
        padding: 16px 0;
        font-size: 14px;
        line-height: 1.5;
        color: rgba(0, 0, 0, 0.87);
      }
      mat-dialog-content {
        min-height: 100px;
      }
    `,
  ],
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, FormsModule],
})
export class CancelReasonDialogComponent {
  cancellationReason = "";
  showError = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { reason: string; viewOnly: boolean },
    private dialogRef: MatDialogRef<CancelReasonDialogComponent>
  ) {
    this.cancellationReason = data.reason || "";
  }

  onConfirm() {
    if (!this.cancellationReason.trim()) {
      this.showError = true;
      return;
    }
    this.dialogRef.close(this.cancellationReason);
  }
}
